import {type History} from "history";
import {searchActions} from "./useSearch";
import {findSuggestions} from "./find-suggestions";
import {useState} from "react";
import {Project} from "../../cdx-models/Project";
import {Suggestion} from "./search-types";

export type SearchInputState = {
  selectedIndex: number;
  suggestions: Suggestion[];
  hasPendingSuggestions: boolean;
  searchValue: string;
  cancelAsyncLoading: (() => void) | null;
  waitingForLoadedTimeoutId: NodeJS.Timeout | null;
};

const initialSeachInputState: SearchInputState = {
  selectedIndex: 0,
  suggestions: [],
  hasPendingSuggestions: false,
  searchValue: "",
  cancelAsyncLoading: null,
  waitingForLoadedTimeoutId: null,
};

const findIndexed = (index: number, suggestions: Suggestion[]) => {
  let suggestion = null;
  let entry = null;
  let offset = 0;
  for (let i = 0; i < suggestions.length; i += 1) {
    const currCat = suggestions[i];
    if (index < offset + currCat.entries.length) {
      suggestion = currCat;
      entry = currCat.entries[index - offset];
      break;
    } else {
      offset += currCat.entries.length;
    }
  }
  return {suggestion, entry};
};

const maintainIndex = (
  wasVisible: boolean,
  prevIndex: number,
  prevSuggs: Suggestion[],
  nextSuggs: Suggestion[]
) => {
  if (!wasVisible) return 0;

  const {suggestion, entry} = findIndexed(prevIndex, prevSuggs);

  if (!entry || !suggestion) return 0;

  let newIndex = 0;
  let foundIndex = 0;
  outer: for (let i = 0; i < nextSuggs.length; i += 1) {
    const {category, entries} = nextSuggs[i];
    if (suggestion.category === category) {
      for (let ii = 0; ii < entries.length; ii += 1) {
        if (entries[ii].key === entry.key) {
          foundIndex = newIndex;
          break outer;
        }
        newIndex += 1;
      }
    } else {
      newIndex += entries.length;
    }
  }
  return foundIndex;
};

const clearState = (state: SearchInputState) => {
  if (state.cancelAsyncLoading) state.cancelAsyncLoading();
  if (state.waitingForLoadedTimeoutId) clearTimeout(state.waitingForLoadedTimeoutId);
  return {
    ...state,
    selectedIndex: 0,
    suggestions: [], // {category, suggestions: [...values]}
    hasPendingSuggestions: false,
    searchValue: "",
    cancelAsyncLoading: null,
    waitingForLoadedTimeoutId: null,
  };
};

type SearchInputParams = {
  history: History;
  closeSearch: () => void;
  projects: Project[];
};

export const useSearchInput = ({history, closeSearch, projects}: SearchInputParams) => {
  const [state, setState] = useState(initialSeachInputState);

  const actions = {
    selectAbove: () => {
      const {selectedIndex} = state;
      if (selectedIndex > 0) setState({...state, selectedIndex: selectedIndex - 1});
    },
    selectBelow: () => {
      const {suggestions, selectedIndex} = state;
      const optsCount = suggestions.reduce((sum, sug) => sum + sug.entries.length, 0);
      if (selectedIndex + 1 < optsCount) {
        setState({...state, selectedIndex: selectedIndex + 1});
      }
    },
    selectIndexed: () => {
      const {suggestions, selectedIndex} = state;
      if (suggestions.length === 0) return;
      const {suggestion, entry} = findIndexed(selectedIndex, suggestions);
      if (!entry || !suggestion) return;
      if (suggestion.isFilter) {
        searchActions.addFilter({
          category: suggestion.category,
          value: entry.value,
        });
      } else {
        suggestion.navigateTo({entry, history});
        closeSearch();
      }
      return setState(clearState(state));
    },
    selectOption: ({suggestion, entry}: {suggestion: Suggestion; entry: any}) => {
      if (suggestion.isFilter) {
        searchActions.addFilter({
          category: suggestion.category,
          value: entry.value,
        });
      } else {
        suggestion.navigateTo({entry, history});
        closeSearch();
      }
      return setState(clearState(state));
    },
    onAsyncSuggestions: ({
      suggestions,
      isLoaded,
    }: {
      suggestions: Suggestion[];
      isLoaded: boolean;
    }) => {
      setState((prev) => {
        if (prev.waitingForLoadedTimeoutId) {
          clearTimeout(prev.waitingForLoadedTimeoutId);
        }
        const waitingForLoadedTimeoutId = null;
        return {
          ...prev,
          selectedIndex: maintainIndex(
            prev.waitingForLoadedTimeoutId === null,
            prev.selectedIndex,
            prev.suggestions,
            suggestions
          ),
          suggestions,
          hasPendingSuggestions: !isLoaded,
          waitingForLoadedTimeoutId,
        };
      });
    },
    onChangeSearchInput: (e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value;
      if (!searchValue.trim()) {
        setState({...clearState(state), searchValue});
      } else {
        if (state.cancelAsyncLoading) state.cancelAsyncLoading();
        if (state.waitingForLoadedTimeoutId) clearTimeout(state.waitingForLoadedTimeoutId);

        const {suggestions, isLoaded, cancelAsyncLoading} = findSuggestions(
          searchValue,
          projects,
          actions.onAsyncSuggestions
        );

        const waitingForLoadedTimeoutId =
          isLoaded || (state.suggestions.length > 0 && state.waitingForLoadedTimeoutId === null)
            ? null
            : setTimeout(() => {
                setState((s2) => ({...s2, waitingForLoadedTimeoutId: null}));
              }, 200);
        setState({
          ...state,
          selectedIndex: maintainIndex(
            state.waitingForLoadedTimeoutId === null,
            state.selectedIndex,
            state.suggestions,
            suggestions
          ),
          suggestions,
          hasPendingSuggestions: !isLoaded,
          searchValue,
          cancelAsyncLoading,
          waitingForLoadedTimeoutId,
        });
      }
    },
  };
  return [state, actions] as const;
};
